import { create } from "zustand"
import {
  getOrganizations as fetchOrganizations,
  getSitesByOrganization as fetchSitesByOrganization,
} from "../services/axiosServices"
import { userStore } from "./userStore"

let activeOrgsPromise = null
let activeSitesPromise = null

const initialState = {
  orgs: [],
  loadingOrgs: false,
  orgsError: null,
  selectedOrgId: null,
  sites: [],
  loadingSites: false,
  sitesError: null,
}

export const organizationStore = create((set, get) => ({
  ...initialState,

  getOrganizations: async (force = false) => {
    const { orgs } = get()
    if (!force && orgs.length > 0) return orgs

    if (activeOrgsPromise) return activeOrgsPromise

    activeOrgsPromise = (async () => {
      set({ loadingOrgs: true, orgsError: null })
      try {
        const res = await fetchOrganizations()
        const list = res?.data?.data || []

        const defaultOrgId =
          userStore.getState().orgId ||
          (list.length > 0 ? String(list[0].id) : null)
        const selectedId = get().selectedOrgId || defaultOrgId

        set({ orgs: list, loadingOrgs: false, selectedOrgId: selectedId })

        const activeOrg = list.find((o) => String(o.id) === String(selectedId))
        if (activeOrg) {
          const orgIdStr = String(activeOrg.id)
          userStore.getState().setUserData({
            orgId: orgIdStr,
            orgName: activeOrg.name,
            orgCreated: "true",
          })
          userStore.getState().fetchUserPermissions()
          get().getSitesByOrganization(orgIdStr)
        }

        return list
      } catch (err) {
        console.error("Failed to load organizations:", err)
        set({
          orgsError: "Failed to load organizations. Please try again.",
          loadingOrgs: false,
        })
        throw err
      } finally {
        activeOrgsPromise = null
      }
    })()

    return activeOrgsPromise
  },

  getSitesByOrganization: async (orgId, force = false) => {
    if (!orgId) {
      set({ sites: [] })
      return []
    }

    if (!force && activeSitesPromise) return activeSitesPromise

    activeSitesPromise = (async () => {
      set({ loadingSites: true, sitesError: null })
      try {
        const res = await fetchSitesByOrganization(orgId)
        const list = res?.data?.data || []
        set({ sites: list, loadingSites: false })
        return list
      } catch (err) {
        console.error("Failed to load sites:", err)
        set({
          sitesError: "Failed to load sites. Please try again.",
          loadingSites: false,
        })
        throw err
      } finally {
        activeSitesPromise = null
      }
    })()

    return activeSitesPromise
  },

  getSiteNamesByIds: (siteIds = []) => {
    const { sites } = get()
    return siteIds.map((siteId) => {
      const site = sites.find((s) => String(s.id) === String(siteId))
      return site ? site.name : null
    })
  },

  setSelectedOrgId: (id) => {
    set({ selectedOrgId: id })
    const activeOrg = get().orgs.find((o) => String(o.id) === String(id))
    if (activeOrg) {
      const orgIdStr = String(activeOrg.id)
      userStore.getState().setUserData({
        orgId: orgIdStr,
        orgName: activeOrg.name,
      })
      userStore.getState().fetchUserPermissions()
      get().getSitesByOrganization(orgIdStr, true)
    }
  },

  clearOrgs: () => set(initialState),
}))
